import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, Buildings, User } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const statusBadge = (s) => {
  const map = {
    draft: "bg-amber-50 text-amber-700 border-amber-200",
    ready: "bg-blue-50 text-blue-700 border-blue-300",
    submitted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  return `text-xs px-2 py-0.5 border font-mono uppercase tracking-wider ${map[s] || "bg-zinc-100 text-zinc-700 border-zinc-200"}`;
};

// Derive a UX-friendly status: a "draft" with 0 placeholders left becomes "ready"
// — visually distinct so users know the proposal is polished and ready to submit.
const effectiveStatus = (p) =>
  p.status === "draft" && (p.placeholders_left ?? 0) === 0 ? "ready" : p.status;

export default function Proposals() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [scope, setScope] = useState("mine");
  const hasCompany = !!user?.company_id;

  useEffect(() => {
    api.get(`/proposals?scope=${scope}`).then((r) => setList(r.data)).catch(() => {});
  }, [scope]);

  const filtered = list.filter((p) => {
    if (filter !== "all" && effectiveStatus(p) !== filter) return false;
    if (!q) return true;
    const t = q.toLowerCase();
    return p.title.toLowerCase().includes(t) || p.grant_title.toLowerCase().includes(t);
  });

  const isCompanyView = scope === "company";

  return (
    <div className="max-w-7xl">
      <div className="gf-overline">Proposals</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2">
        {isCompanyView ? "Company proposals." : "My proposals."}
      </h1>

      {hasCompany && (
        <div className="mt-6 inline-flex border border-zinc-200 bg-white" data-testid="proposals-scope">
          <button
            onClick={() => setScope("mine")}
            data-testid="scope-mine"
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 ${scope === "mine" ? "bg-blue-700 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
          >
            <User size={14} /> Mine
          </button>
          <button
            onClick={() => setScope("company")}
            data-testid="scope-company"
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 ${scope === "company" ? "bg-blue-700 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
          >
            <Buildings size={14} /> Company
          </button>
        </div>
      )}

      <div className="mt-6 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <div className="relative md:w-96">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            data-testid="proposals-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by title or grant…"
            className="gf-sharp pl-10 border-zinc-300 bg-white"
          />
        </div>
        <div className="flex border border-zinc-200 bg-white" data-testid="proposals-filter">
          {["all", "draft", "ready", "submitted"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-wider ${filter === f ? "bg-blue-700 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 border border-zinc-200 bg-white">
        {filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="gf-overline">No proposals</div>
            <p className="text-zinc-600 mt-2 text-sm">
              {list.length === 0
                ? isCompanyView
                  ? "Nobody in your company has drafted a proposal yet."
                  : "Start by searching for a grant and generating your first draft."
                : "No proposals match the current filters."}
            </p>
            <Link to="/search" className="inline-block mt-5 bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wide px-6 py-3 gf-sharp" data-testid="cta-find-grants-list">
              Find grants
            </Link>
          </div>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-zinc-200 text-left gf-overline">
                <th className="px-6 py-3">Title</th>
                <th className="px-6 py-3">Grant</th>
                <th className="px-6 py-3">Agency</th>
                {isCompanyView && <th className="px-6 py-3">Owner</th>}
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Updated</th>
                <th className="px-6 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`proposal-row-${p.id}`}>
                  <td className="px-6 py-4 font-semibold max-w-xs truncate">{p.title}</td>
                  <td className="px-6 py-4 text-zinc-600 max-w-xs truncate">{p.grant_title}</td>
                  <td className="px-6 py-4 text-zinc-500 font-mono text-xs">{p.grant_agency}</td>
                  {isCompanyView && (
                    <td className="px-6 py-4 text-zinc-600 text-xs">
                      <div className="font-semibold">{p.owner_name || "—"}</div>
                      <div className="font-mono text-zinc-500">{p.owner_email || ""}</div>
                    </td>
                  )}
                  <td className="px-6 py-4"><span className={statusBadge(effectiveStatus(p))}>{effectiveStatus(p)}</span></td>
                  <td className="px-6 py-4 text-zinc-500 font-mono text-xs">{new Date(p.updated_at).toLocaleDateString()}</td>
                  <td className="px-6 py-4 text-right">
                    <Link to={`/proposals/${p.id}`} className="text-blue-700 font-semibold hover:underline" data-testid={`row-open-${p.id}`}>
                      Open →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
